import { useState } from "react";

export default function DependencyGraph({ deps }) {
  const [selected, setSelected] = useState(null);
  const centerX = 300;
  const centerY = 150;
  const radius = 120;
  const others = deps.nodes.filter((n) => n.kind !== "self");
  const positions = { [deps.nodes.find((n) => n.kind === "self")?.id]: { x: centerX, y: centerY } };
  others.forEach((n, i) => {
    const angle = (i / others.length) * Math.PI * 2 - Math.PI / 2;
    positions[n.id] = { x: centerX + Math.cos(angle) * radius, y: centerY + Math.sin(angle) * radius };
  });
  const kindClass = {
    self: "node-self",
    critical: "node-critical",
    supporting: "node-supporting",
    downstream: "node-downstream",
    conflict: "node-conflict",
  };

  return (
    <div className="dep-layout" data-testid="dependency-graph">
      <div className="dep-graph">
        <svg viewBox="0 0 600 300" width="100%" height="300">
          {deps.edges.map(([a, b], i) => {
            const A = positions[a];
            const B = positions[b];
            if (!A || !B) return null;
            return <line key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="#2a3447" strokeWidth="1" />;
          })}
          {deps.nodes.map((n) => {
            const p = positions[n.id];
            if (!p) return null;
            const active = selected?.id === n.id;
            return (
              <g
                key={n.id}
                className={`dep-node ${kindClass[n.kind] || ""} ${active ? "dep-node-active" : ""}`}
                onClick={() => setSelected(active ? null : n)}
                data-testid={`dep-node-${n.id.toLowerCase()}`}
                style={{ cursor: "pointer" }}
              >
                <circle cx={p.x} cy={p.y} r={n.kind === "self" ? 34 : 24} />
                <text x={p.x} y={p.y + 4} textAnchor="middle">{n.id}</text>
                <text x={p.x} y={p.y + (n.kind === "self" ? 52 : 42)} textAnchor="middle" className="dep-label">{n.label}</text>
                {n.owner === "unknown" && <text x={p.x + 24} y={p.y - 18} className="dep-warn">!</text>}
              </g>
            );
          })}
        </svg>
        <div className="dep-legend">
          <span><i className="dot-critical" /> Critical dependency</span>
          <span><i className="dot-supporting" /> Supporting (owner unknown)</span>
          <span><i className="dot-downstream" /> Downstream</span>
          <span><i className="dot-conflict" /> Conflict</span>
        </div>
      </div>
      <aside className={`dep-drawer ${selected ? "open" : ""}`} data-testid="dep-impact-drawer">
        {selected ? (
          <>
            <p className="kicker">DEPENDENCY IMPACT · {selected.id}</p>
            <h3>{selected.label}</h3>
            <div className={`dep-kind-badge kind-${selected.kind}`}>{selected.kind.toUpperCase()}</div>
            <p className="muted">{selected.impact?.summary || "Dependency mapped without impact metadata."}</p>
            <div className="dep-impact-block">
              <h4>Affected applications</h4>
              {(selected.impact?.apps || []).length ? (
                selected.impact.apps.map((a) => <span key={a} className="dep-chip" data-testid={`dep-impact-app-${a}`}>{a}</span>)
              ) : <p className="muted small-note">None recorded.</p>}
            </div>
            <div className="dep-impact-block">
              <h4>Migration waves</h4>
              {(selected.impact?.waves || []).length ? (
                selected.impact.waves.map((w) => <span key={w} className="dep-chip cyan" data-testid={`dep-impact-wave-${w}`}>{w}</span>)
              ) : <p className="muted small-note">Not in an active wave.</p>}
            </div>
            <div className="dep-impact-block">
              <h4>Blocked gates</h4>
              {(selected.impact?.blocks || []).length ? (
                selected.impact.blocks.map((g) => <span key={g} className="dep-chip amber" data-testid={`dep-impact-block-${g}`}>{g}</span>)
              ) : <span className="dep-chip green">No gates blocked</span>}
            </div>
            <button data-testid="dep-drawer-close" className="text-button" type="button" onClick={() => setSelected(null)}>Close</button>
          </>
        ) : (
          <div className="dep-drawer-empty">
            <p className="kicker">DEPENDENCY IMPACT</p>
            <p className="muted">Select any node in the graph to inspect affected applications, waves and blocked gates.</p>
          </div>
        )}
      </aside>
    </div>
  );
}
